import React from 'react';
import { BathFixtureShell, Rect, Line } from './BathFixtureFrame';
import {
  WALL_HUNG_SINK_SHROUD_INSET_SIDE,
  wallHungSinkProfile,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Wall-hung bathroom sink — the compact one-piece porcelain basin the
 * vanity assembly falls back to when its cabinet is too small for any
 * real sink (`shared-lib/vanity-assembly/wallHungSink.js`). Twin of
 * `proceduralRenderers/wallHungSinkRenderer.js`; every proportion comes
 * from proceduralSpecs.js (WALL-HUNG SINK section) so the two cannot
 * drift.
 *
 * Reached two ways: the gallery card's `wall-hung-sink` type, and a
 * real `type: 'sink'` object with `sinkType: 'wall-mount'`, which
 * `SinkSchematic` hands here (the 3D side routes the same object to
 * the wall-hung renderer through `resolveProceduralType`).
 *
 * Front: the basin body — rim to the underside of the bowl floor —
 * as a full-width rectangle, and the shroud below it, narrower by the
 * side inset. The bowl, deck and drain are inside the rim and hidden
 * in a front elevation.
 *
 * Sides: the depth profile, wall at x = 0 in the un-mirrored frame,
 * room-side at x = depth. Rim flat from the wall to the front lip; the
 * front drops the basin height; the underside steps back to the
 * shroud, which tapers further toward the wall on its way down.
 */
export function WallHungSinkSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const stroke = { strokeColor, strokeWidth };
  if (view === 'left' || view === 'right') {
    return (
      <SideView view={view} depth={depth} height={height} stroke={stroke} />
    );
  }
  const { basinBottomY } = wallHungSinkProfile({ height, depth });
  const inset = WALL_HUNG_SINK_SHROUD_INSET_SIDE;
  return (
    <BathFixtureShell width={width} height={height}>
      {/* Basin body: rim down to the underside of the bowl floor. */}
      <Rect x={0} y={0} width={width} height={basinBottomY} {...stroke} />
      {/* Shroud: narrower than the basin, down to the bottom of the unit. */}
      <Rect
        x={inset}
        y={basinBottomY}
        width={width - inset * 2}
        height={height - basinBottomY}
        {...stroke}
      />
    </BathFixtureShell>
  );
}
WallHungSinkSchematic.supportsSideViews = true;

/** Closed outline drawn as segments so the drawn-extent test can read it. */
function Outline({ points, stroke }) {
  return points.map(([x1, y1], i) => {
    const [x2, y2] = points[(i + 1) % points.length];
    return <Line key={i} x1={x1} y1={y1} x2={x2} y2={y2} {...stroke} />;
  });
}

function SideView({ view, depth, height, stroke }) {
  const { basinBottomY, shroudTopZ, shroudBottomZ } = wallHungSinkProfile({
    height,
    depth,
  });
  // Clockwise from the rim at the wall: rim, front lip, basin underside,
  // shroud face (tapering back), bottom, back up the wall.
  const points = [
    [0, 0],
    [depth, 0],
    [depth, basinBottomY],
    [shroudTopZ, basinBottomY],
    [shroudBottomZ, height],
    [0, height],
  ];
  return (
    <BathFixtureShell width={depth} height={height}>
      <g
        transform={
          view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
        }
      >
        <Outline points={points} stroke={stroke} />
      </g>
    </BathFixtureShell>
  );
}
